import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Link2, Check, Send } from 'lucide-react';

/**
 * EventShareControl — a compact Share button that opens a two-option menu:
 *   1. "Share event" — full native share sheet (title + details + link) so the
 *      recipient app can render the rich preview (image/title). Falls back to
 *      copying the link on browsers without the Web Share API.
 *   2. "Copy link"   — copies ONLY the clean event URL to the clipboard, with no
 *      title/description, so the user can paste a bare link anywhere.
 *
 * Props: url (required), title, text, testId, className.
 */
const EventShareControl = ({ url, title, text, testId = 'event', className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!url || typeof url !== 'string' || !url.trim()) return null;

  const copyToClipboard = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = val;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const shareFull = async (e) => {
    e.stopPropagation();
    setOpen(false);
    const payload = { title: title || 'Find-2-Day', text: text || title || '', url };
    if (navigator.share) {
      try { await navigator.share(payload); return; }
      catch (err) { if (err && err.name === 'AbortError') return; }
    }
    await copyToClipboard(url); // desktop fallback
  };

  const copyLinkOnly = async (e) => {
    e.stopPropagation();
    setOpen(false);
    await copyToClipboard(url);
  };

  return (
    <div className={`relative ${className}`} ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium
                   bg-white/80 hover:bg-white text-gray-700 border border-gray-200 transition-colors"
        data-testid={`share-menu-${testId}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Share2 className="h-3.5 w-3.5" />}
        {copied ? t('copied', 'Copied!') : t('share', 'Share')}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 w-52 bg-white rounded-xl shadow-lg ring-1 ring-gray-100 z-50 overflow-hidden"
          data-testid={`share-menu-list-${testId}`}
        >
          <button
            type="button"
            onClick={shareFull}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50"
            data-testid={`share-full-${testId}`}
          >
            <Send className="h-4 w-4 text-purple-500 shrink-0" />
            <div>
              <p className="font-medium text-gray-900">{t('shareEvent', 'Share event')}</p>
              <p className="text-[11px] text-gray-500">{t('shareEventHint', 'Title, details & link')}</p>
            </div>
          </button>
          <button
            type="button"
            onClick={copyLinkOnly}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50 border-t border-gray-100"
            data-testid={`share-linkonly-${testId}`}
          >
            <Link2 className="h-4 w-4 text-purple-500 shrink-0" />
            <div>
              <p className="font-medium text-gray-900">{t('copyLinkOnly', 'Copy link')}</p>
              <p className="text-[11px] text-gray-500">{t('copyLinkOnlyHint', 'Just the URL, no preview')}</p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default EventShareControl;
